// noinspection DuplicatedCode
import styles from './ScenarioTwo.module.scss';
import ChartWrapper from 'components/chart-wrapper/ChartWrapper';
import ContentContainer from 'components/content-container/ContentContainer';
import InfiniteTable from 'components/infinite-table/InfiniteTable';
import RmCardsGroup from 'components/rm-cards-group/RmCardsGroup';
import { detailedViewTable } from 'constants/scenarioTwo';
import ReactECharts from 'echarts-for-react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFiltersAllValues,
    selectMarketValue,
    selectRmValue,
    selectScenarioTwoBreachCategoryData,
    selectScenarioTwoOutliersData,
    selectScenarioTwoTopFiveRmsData,
    selectScenarioTwoTrendData,
    selectTeamValue,
    selectTimeslicerEndMonth,
    selectTimeslicerIsSameMonth,
    selectTimeslicerStartMonth,
    selectZoneValue
} from 'selectors/selectors';
import {
    fetchScenarioTwoBreachCategory,
    fetchScenarioTwoOutliers,
    fetchScenarioTwoStats,
    fetchScenarioTwoTopFiveRmsData,
    fetchScenarioTwoTrendData
} from 'slices/ScenarioTwoSlice';
import {
    ChartType,
    RMCardGroupDataType,
    Scenarios,
    ScenarioType,
    TopFiveRmsCategory
} from 'types/types';
import { generateRequestParamsForScenarioApi } from 'utils/api';
import {
    scenarioTwoNoResultScatterPlotOptions,
    scenarioTwoNoResultTrendAnalysisChartOptions,
    scenarioTwoScatterPlotOptions,
    scenarioTwoStackedBarChartOptions,
    scenarioTwoTrendAnalysisChartOptions
} from 'utils/charts';
import { areAllFilterValuesNumberValues } from 'utils/filters';
import {
    calculateStartEndMonth,
    generateUpperRightDateRangeContent
} from 'utils/common';

const ScenarioTwo = () => {
    const dispatch = useDispatch();

    // Selectors
    const tsStart = useSelector(selectTimeslicerStartMonth);
    const tsEnd = useSelector(selectTimeslicerEndMonth);
    const isSameMonth = useSelector(selectTimeslicerIsSameMonth);
    const allValues = useSelector(selectFiltersAllValues);

    const dates = calculateStartEndMonth(tsStart, tsEnd, isSameMonth);
    const { start, end } = { ...dates };
    const nonSpikeDates = calculateStartEndMonth(
        tsStart,
        tsEnd,
        isSameMonth,
        true
    );
    const { start: nonSpikeStart, end: nonSpikeEnd } = { ...nonSpikeDates };

    const zoneValue = useSelector(selectZoneValue);
    const marketValue = useSelector(selectMarketValue);
    const teamValue = useSelector(selectTeamValue);
    const rmValue = useSelector(selectRmValue);

    const outliersData = useSelector(selectScenarioTwoOutliersData);
    const breachCategoryData = useSelector(selectScenarioTwoBreachCategoryData);
    const trendData = useSelector(selectScenarioTwoTrendData);
    const topFiveRmsData = useSelector(selectScenarioTwoTopFiveRmsData);

    useEffect(() => {
        if (tsStart && tsEnd) {
            if (
                areAllFilterValuesNumberValues({
                    startMonth: tsStart.epoch,
                    endMonth: tsEnd.epoch,
                    zone: zoneValue,
                    market: marketValue,
                    team: teamValue,
                    rm: rmValue
                })
            ) {
                dispatch(
                    fetchScenarioTwoStats(
                        generateRequestParamsForScenarioApi(
                            ScenarioType.Stats,
                            Scenarios.Two,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                // fetchScenarioTwoOutliers
                dispatch(
                    fetchScenarioTwoOutliers(
                        generateRequestParamsForScenarioApi(
                            ChartType.Outliers,
                            Scenarios.Two,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch,
                            allValues
                        )
                    )
                );
                dispatch(
                    fetchScenarioTwoBreachCategory(
                        generateRequestParamsForScenarioApi(
                            ChartType.BreachCategory,
                            Scenarios.Two,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
                dispatch(
                    fetchScenarioTwoTrendData(
                        generateRequestParamsForScenarioApi(
                            ChartType.Trend,
                            Scenarios.Two,
                            marketValue,
                            teamValue,
                            rmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );

                const checkedRmValue =
                    allValues.rm.value === rmValue ? null : rmValue;
                dispatch(
                    fetchScenarioTwoTopFiveRmsData(
                        generateRequestParamsForScenarioApi(
                            ScenarioType.TopFiveRms,
                            Scenarios.Two,
                            marketValue,
                            teamValue,
                            checkedRmValue,
                            tsStart.epoch,
                            tsEnd.epoch
                        )
                    )
                );
            }
        }
    }, [marketValue, teamValue, rmValue, tsStart, tsEnd]);

    const { breachCategory } = { ...breachCategoryData };
    const { scenarioTwoTrendAnalysisChartData } = { ...trendData };
    const { data } = { ...scenarioTwoTrendAnalysisChartData };
    const { concentration, portfolio } = { ...topFiveRmsData };

    const scatterPlotChartRef = useRef(null);
    useEffect(() => {
        scatterPlotChartRef.current.getEchartsInstance().dispose();
        if (outliersData && outliersData.outliers.length > 0) {
            scatterPlotChartRef.current
                .getEchartsInstance()
                .setOption(scenarioTwoScatterPlotOptions(outliersData));
        } else {
            scatterPlotChartRef.current
                .getEchartsInstance()
                .setOption(scenarioTwoNoResultScatterPlotOptions());
        }
    }, [outliersData]);

    const trendAnalysisChartRef = useRef(null);
    useEffect(() => {
        trendAnalysisChartRef.current.getEchartsInstance().dispose();
        if (data && data.filter((d) => d.month > 0 && d.year > 0).length > 0) {
            trendAnalysisChartRef.current
                .getEchartsInstance()
                .setOption(scenarioTwoTrendAnalysisChartOptions(data));
        } else {
            trendAnalysisChartRef.current
                .getEchartsInstance()
                .setOption(scenarioTwoNoResultTrendAnalysisChartOptions());
        }
    }, [data]);

    return (
        <ContentContainer>
            <div className={styles.chartsContainer}>
                <ChartWrapper comboClass={styles.rightMargin}>
                    <ReactECharts
                        option={
                            outliersData && outliersData.outliers.length > 0
                                ? scenarioTwoScatterPlotOptions(outliersData)
                                : scenarioTwoNoResultScatterPlotOptions()
                        }
                        style={{ width: '100%', height: '100%' }}
                        ref={scatterPlotChartRef}
                    ></ReactECharts>
                </ChartWrapper>

                <ChartWrapper comboClass={styles.rightMargin}>
                    <ReactECharts
                        option={scenarioTwoStackedBarChartOptions(
                            breachCategory
                        )}
                        notMerge={true}
                        style={{ width: '100%', height: '100%' }}
                    ></ReactECharts>
                </ChartWrapper>

                <ChartWrapper>
                    <ReactECharts
                        option={
                            data &&
                            data.filter((d) => d.month > 0 && d.year > 0)
                                .length > 0
                                ? scenarioTwoTrendAnalysisChartOptions(data)
                                : scenarioTwoNoResultTrendAnalysisChartOptions()
                        }
                        style={{ width: '100%', height: '100%' }}
                        ref={trendAnalysisChartRef}
                    ></ReactECharts>
                </ChartWrapper>
            </div>
            <div className={styles.rmCardsContainer}>
                <RmCardsGroup
                    breachCategory={TopFiveRmsCategory.Concentration}
                    width={'49%'}
                    data={concentration}
                    dataType={RMCardGroupDataType.Alerts}
                    title={'Top 5 RMs (By Concentration Risk Alert)'}
                    upperRightContent={generateUpperRightDateRangeContent({
                        tsStart,
                        tsEnd,
                        nonSpikeStart,
                        nonSpikeEnd,
                        start,
                        end,
                        shouldShowSameWhenOneMonth: true
                    })}
                    footerText={'% of Alerts'}
                />
                <RmCardsGroup
                    breachCategory={TopFiveRmsCategory.Portfolio}
                    width={'49%'}
                    data={portfolio}
                    dataType={RMCardGroupDataType.Alerts}
                    title={'Top 5 RMs (By Portfolio Risk Mismatch Alert)'}
                    upperRightContent={generateUpperRightDateRangeContent({
                        tsStart,
                        tsEnd,
                        nonSpikeStart,
                        nonSpikeEnd,
                        start,
                        end,
                        shouldShowSameWhenOneMonth: true
                    })}
                    footerText={'% of Alerts'}
                />
            </div>
            <div className={styles.tableContainer}>
                <div className={styles.tableTitle}>Detailed View</div>
                <InfiniteTable
                    zone={zoneValue}
                    market={marketValue}
                    team={teamValue}
                    rm={rmValue}
                    start={tsStart}
                    end={tsEnd}
                    allValues={allValues}
                    columns={detailedViewTable}
                    scenario={Scenarios.Two}
                />
            </div>
        </ContentContainer>
    );
};

export default ScenarioTwo;
